import { useState } from 'react';

import { useSshKeys } from '../../hooks/use-ssh-keys';
import { describeApiError } from '../../lib/api-client';
import { Empty, Td, Th, fmtWhen, mono, primaryButton, secondaryButton, tableStyle, theadRowStyle } from '../proxy-vault/ui';

import { AddSshKeyModal, keyTypeLabel } from './AddSshKeyModal';

import type { UserSshKeyResponse } from '../../lib/api-client';

interface Props {
  userId: string;
  /** The user's email — shown in the add-key dialog so an admin knows whose key they paste. */
  userLabel: string;
  /** Self, or an admin holding manage_users / full_access over the user's organization tree. */
  canManage: boolean;
  /** `table` = the SFTP pane; `compact` = the block inside the Team user popover. */
  variant?: 'table' | 'compact';
  /** Fired with the new active list after an add or a revoke (the Accounts tab refreshes its counts). */
  onChanged?: (keys: UserSshKeyResponse[]) => void;
}

const smallButton: React.CSSProperties = { ...secondaryButton, padding: '3px 8px', fontSize: 11 };
const dangerButton: React.CSSProperties = { ...smallButton, color: 'var(--kind-failure)' };

/**
 * A user's active SSH keys with add + two-step revoke. Revoked keys are hidden; a revoke is
 * immediate for new logins and ends a live session within one refresh (≤ 5 min).
 */
export function SshKeyList({ userId, userLabel, canManage, variant = 'table', onChanged }: Props) {
  const { keys, status, error, forbidden, addKey, revokeKey } = useSshKeys(userId);
  const [adding, setAdding] = useState(false);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const revoke = async (id: string) => {
    setBusyId(id);
    setActionError(null);
    try {
      await revokeKey(id);
      setConfirming(null);
      onChanged?.(keys.filter((k) => k.id !== id));
    } catch (err) {
      setActionError(describeApiError(err, 'The key was not revoked'));
    } finally {
      setBusyId(null);
    }
  };

  const actions = (k: UserSshKeyResponse) => {
    if (!canManage) return null;
    if (confirming === k.id) {
      return (
        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: 'var(--kind-failure)' }}>Confirm?</span>
          <button
            type="button"
            data-testid={`sshkey-confirm-${k.id}`}
            style={dangerButton}
            onClick={() => void revoke(k.id)}
            disabled={busyId === k.id}
          >
            {busyId === k.id ? 'Revoking…' : 'Yes, revoke'}
          </button>
          <button type="button" data-testid={`sshkey-cancel-${k.id}`} style={smallButton} onClick={() => setConfirming(null)}>
            Cancel
          </button>
        </span>
      );
    }
    return (
      <button type="button" data-testid={`sshkey-revoke-${k.id}`} style={smallButton} onClick={() => setConfirming(k.id)}>
        Revoke
      </button>
    );
  };

  const addButton = canManage && (
    <button
      type="button"
      data-testid="sshkey-add"
      style={variant === 'compact' ? smallButton : primaryButton}
      onClick={() => setAdding(true)}
    >
      + Add key
    </button>
  );

  const modal = adding && (
    <AddSshKeyModal
      userLabel={userLabel}
      onCancel={() => setAdding(false)}
      onAdd={async (body) => {
        const created = await addKey(body);
        onChanged?.([created, ...keys]);
        return created;
      }}
    />
  );

  if (forbidden) {
    return <div style={{ fontSize: 12, color: 'var(--text-mute)' }}>You may not view this user's keys.</div>;
  }

  if (variant === 'compact') {
    return (
      <div data-testid="sshkey-list-compact">
        {status === 'error' && (
          <div role="alert" style={{ fontSize: 11, color: 'var(--kind-failure)' }}>
            {error}
          </div>
        )}
        {status === 'success' && keys.length === 0 && (
          <div style={{ fontSize: 11, color: 'var(--text-mute)' }}>No SSH keys — no SFTP login possible yet.</div>
        )}
        {keys.map((k) => (
          <div
            key={k.id}
            data-testid={`sshkey-row-${k.id}`}
            style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, padding: '4px 0', color: 'var(--text)' }}
          >
            <span style={{ fontWeight: 500 }}>{k.name}</span>
            <span style={{ color: 'var(--text-soft)' }}>{keyTypeLabel(k.key_type)}</span>
            <span title={k.fingerprint} style={{ fontFamily: mono, color: 'var(--text-soft)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {k.fingerprint}
            </span>
            {actions(k)}
          </div>
        ))}
        {actionError && (
          <div role="alert" style={{ fontSize: 11, color: 'var(--kind-failure)' }}>
            {actionError}
          </div>
        )}
        <div style={{ marginTop: 6 }}>{addButton}</div>
        {modal}
      </div>
    );
  }

  return (
    <div data-testid="sshkey-list">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
        <div style={{ fontSize: 11, color: 'var(--text-soft)', fontWeight: 600 }}>
          <span style={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>Keys for</span>{' '}
          <span style={{ color: 'var(--text)' }}>{userLabel}</span>
        </div>
        <span style={{ fontSize: 11, color: 'var(--text-mute)' }}>
          {status === 'success' ? `${keys.length} active` : status === 'loading' ? 'loading…' : ''}
        </span>
        <div style={{ marginLeft: 'auto' }}>{addButton}</div>
      </div>
      {status === 'error' && (
        <div role="alert" style={{ fontSize: 12, color: 'var(--kind-failure)', marginBottom: 8 }}>
          {error}
        </div>
      )}
      {status === 'success' && keys.length === 0 ? (
        <Empty>No keys yet. Upload a public key to enable SFTP logins for this account.</Empty>
      ) : (
        <table style={tableStyle}>
          <thead>
            <tr style={theadRowStyle}>
              <Th first>Label</Th>
              <Th>Type</Th>
              <Th>Fingerprint</Th>
              <Th>Last login</Th>
              <Th>{' '}</Th>
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k.id} data-testid={`sshkey-row-${k.id}`} style={{ borderTop: '1px solid var(--border)' }}>
                <Td first>{k.name}</Td>
                <Td>{keyTypeLabel(k.key_type)}</Td>
                <Td isMono>{k.fingerprint}</Td>
                <Td>{k.last_used_at ? fmtWhen(k.last_used_at) : 'never'}</Td>
                <Td>{actions(k)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {actionError && (
        <div role="alert" style={{ fontSize: 12, color: 'var(--kind-failure)', marginTop: 8 }}>
          {actionError}
        </div>
      )}
      <div style={{ fontSize: 11, color: 'var(--text-mute)', marginTop: 10, lineHeight: 1.5 }}>
        Revoked keys are hidden; a revoke is immediate for new logins and ends a live session within five minutes.
        Uploading a key never grants access on its own — the role must carry SFTP access.
      </div>
      {modal}
    </div>
  );
}
